<template>
  <div class="site-settings">
    <header class="site-settings-hero">
      <div>
        <p class="site-settings-kicker">پیکربندی فروشگاه</p>
        <h1>تنظیمات سایت</h1>
        <p>هر تنظیم در دسته خودش است. وضعیت‌های صفر و یک با متن نمایش داده می‌شوند و اطلاعات درگاه در ویرایش، مورد به مورد پر می‌شود.</p>
      </div>
      <router-link :to="{ name: 'CreateSiteInfo' }" class="site-settings-create">
        تنظیمات جدید
      </router-link>
    </header>

    <div class="site-settings-toolbar">
      <input
          v-model="query"
          type="search"
          class="site-settings-search"
          placeholder="جستجو در نام یا مقدار تنظیمات"
      />
      <div class="site-settings-stats">
        <span>{{ filteredItems.length }} مورد</span>
      </div>
    </div>

    <div class="site-settings-cats">
      <button
          type="button"
          class="site-settings-cat"
          :class="{active: activeCategory === 'all'}"
          @click="activeCategory = 'all'"
      >
        همه
        <small>{{ filteredItems.length }}</small>
      </button>
      <button
          v-for="category in categoriesWithItems"
          :key="category.id"
          type="button"
          class="site-settings-cat"
          :class="{active: activeCategory === category.id}"
          @click="activeCategory = category.id"
      >
        <b-icon :icon="category.icon"></b-icon>
        {{ category.title }}
        <small>{{ category.count }}</small>
      </button>
    </div>

    <section v-for="group in visibleGroups" :key="group.id" class="site-settings-group">
      <div class="site-settings-group-head">
        <div class="site-settings-group-icon">
          <b-icon :icon="group.icon"></b-icon>
        </div>
        <div>
          <h2>{{ group.title }}</h2>
          <p>{{ group.description }}</p>
        </div>
      </div>

      <div class="site-settings-grid">
        <article v-for="row in group.items" :key="row.item.id" class="site-setting-card">
          <div class="site-setting-top">
            <h3>{{ row.fa }}</h3>
            <div class="site-setting-actions">
              <router-link
                  :to="{ name: 'UpdateSiteInfo', params: { id: row.item.id } }"
                  v-b-tooltip.hover
                  title="ویرایش"
              >
                <b-icon icon="pencil"></b-icon>
              </router-link>
              <button
                  v-if="row.gateway && gatewayRulesRow"
                  type="button"
                  v-b-tooltip.hover
                  :title="row.rule.enabled ? 'غیرفعال کردن درگاه (اطلاعات حذف نمی‌شود)' : 'فعال کردن درگاه'"
                  :class="row.rule.enabled ? 'site-setting-power is-on' : 'site-setting-power is-off'"
                  :disabled="togglingGateway === row.gateway"
                  @click="toggleGateway(row)"
              >
                <b-icon icon="power"></b-icon>
              </button>
              <button
                  v-if="isDeletable(row.item)"
                  type="button"
                  v-b-tooltip.hover
                  title="حذف"
                  @click="deleteFunction(row.item)"
              >
                <b-icon icon="trash"></b-icon>
              </button>
            </div>
          </div>

          <p v-if="row.hint" class="site-setting-hint">{{ row.hint }}</p>

          <div class="site-setting-value">
            <img v-if="row.shown.type === 'image'" :src="row.shown.src" alt=""/>
            <span
                v-else-if="row.shown.type === 'choice'"
                class="site-setting-pill"
                :class="row.shown.toggle ? (row.shown.on ? 'is-on' : 'is-off') : 'is-neutral'"
            >
              {{ row.shown.text }}
            </span>
            <span v-else>{{ row.shown.text }}</span>
          </div>

          <div v-if="row.rule && (!row.rule.enabled || row.rule.categories.length)" class="site-setting-flags">
            <span v-if="!row.rule.enabled" class="site-setting-pill is-disabled">درگاه غیرفعال است</span>
            <span v-if="row.rule.categories.length" class="site-setting-pill is-neutral">
              فقط برای {{ row.rule.categories.length }} دسته‌بندی مجاز
            </span>
          </div>
        </article>
      </div>
    </section>

    <div v-if="!visibleGroups.length" class="site-settings-empty">
      تنظیماتی با این عبارت پیدا نشد.
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from "vuex";
import {
  siteInfoCategories,
  metaOf,
  displaySiteInfoValue,
} from "../../components/dashboard/SiteInfo/siteInfoCatalog";
import {
  GATEWAY_RULES_KEY,
  findGatewayRulesRow,
  gatewayOfSiteInfo,
  gatewayRuleOf,
  parseGatewayRules,
  saveGatewayRule,
} from "../../components/dashboard/SiteInfo/gatewayRules";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";

export default {
  name: "SiteInfo",
  data() {
    return {
      query: "",
      activeCategory: "all",
      togglingGateway: "",
    };
  },
  computed: {
    ...mapGetters({
      siteInfosRaw: "SiteInfo/getSiteInfos",
      attributes: "SiteInfo/getAttributes",
    }),
    siteInfos() {
      const attrs = this.attributes || {};
      const list = this.siteInfosRaw || [];
      // gateway_rules is edited inside each gateway's own page, so it has no card of its own
      return list.filter((item) => item.name !== GATEWAY_RULES_KEY && Object.prototype.hasOwnProperty.call(attrs, item.name));
    },
    gatewayRulesRow() {
      return findGatewayRulesRow(this.siteInfosRaw);
    },
    gatewayRules() {
      return parseGatewayRules(this.gatewayRulesRow ? this.gatewayRulesRow.value : null);
    },
    filteredItems() {
      const query = this.query.trim();
      return this.siteInfos
          .map((item) => {
            const meta = metaOf(item.name);
            const shown = displaySiteInfoValue(item);
            const gateway = gatewayOfSiteInfo(item.name);
            return {
              gateway,
              rule: gateway ? gatewayRuleOf(this.gatewayRules, gateway) : null,
              item,
              fa: this.nameFa(item),
              hint: meta.hint,
              category: meta.category || "other",
              shown,
            };
          })
          .filter((row) => {
            if (!query) return true;
            const haystack = `${row.fa} ${row.item.name} ${row.shown.text} ${row.hint}`;
            return haystack.includes(query);
          });
    },
    categoriesWithItems() {
      return siteInfoCategories
          .map((category) => ({
            ...category,
            count: this.filteredItems.filter((row) => row.category === category.id).length,
          }))
          .filter((category) => category.count > 0);
    },
    visibleGroups() {
      return this.categoriesWithItems.filter((category) => {
        return this.activeCategory === "all" || this.activeCategory === category.id;
      }).map((category) => ({
        ...category,
        items: this.filteredItems.filter((row) => row.category === category.id),
      }));
    },
  },
  methods: {
    ...mapActions({
      deleteSiteInfo: "SiteInfo/deleteSiteInfo",
    }),
    deleteFunction(item) {
      if (confirm("آیا از حذف این تنظیمات اطمینان دارید؟")) {
        this.deleteSiteInfo(item);
      }
    },
    nameFa(item) {
      const attrs = this.attributes || {};
      return attrs[item.name] && attrs[item.name].fa ? attrs[item.name].fa : item.name;
    },
    async toggleGateway(row) {
      const enable = !row.rule.enabled;
      if (!enable && !confirm("این درگاه غیرفعال شود؟ اطلاعات درگاه حذف نمی‌شود و هر زمان می‌توانید دوباره فعالش کنید.")) {
        return;
      }
      this.togglingGateway = row.gateway;
      try {
        await saveGatewayRule(this.gatewayRulesRow, row.gateway, {enabled: enable, categories: row.rule.categories});
        await this.$store.dispatch("SiteInfo/getSiteInfos");
        toast("ذخیره شد", enable ? "درگاه فعال شد" : "درگاه غیرفعال شد", "success");
      } catch (e) {
        console.log(e);
        toast("خطا", "تغییر وضعیت درگاه انجام نشد", "danger");
      } finally {
        this.togglingGateway = "";
      }
    },
    isDeletable(item) {
      const attrs = this.attributes || {};
      return !!(attrs[item.name] && attrs[item.name].delete);
    },
  },
  mounted() {
    this.$store.dispatch("SiteInfo/getSiteInfos");
  },
};
</script>

<style scoped>
.site-settings {
  text-align: right;
}

.site-settings-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(120deg, #1e1e1e 0%, #3b1d2a 42%, #eb3d60 100%);
  color: #fff;
  border-radius: 24px;
  padding: 26px 28px;
  margin-bottom: 18px;
  box-shadow: 0 18px 36px rgba(235, 61, 96, 0.22);
}

.site-settings-kicker {
  opacity: 0.75;
  font-size: 13px;
  margin: 0 0 6px;
}

.site-settings-hero h1 {
  font-size: 28px;
  font-weight: 800;
  margin: 0 0 8px;
}

.site-settings-hero p {
  margin: 0;
  max-width: 640px;
  opacity: 0.88;
  line-height: 1.8;
}

.site-settings-create {
  background: rgba(255, 255, 255, 0.16);
  color: #fff !important;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 800;
  text-decoration: none !important;
  white-space: nowrap;
}

.site-settings-create:hover {
  background: rgba(255, 255, 255, 0.26);
}

.site-settings-toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.site-settings-search {
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 252, 250, 0.8);
  border-radius: 14px;
  padding: 12px 14px;
  outline: none;
}

.site-settings-stats {
  color: #6b7280;
  font-weight: 700;
}

.site-settings-cats {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 8px;
  margin-bottom: 18px;
}

.site-settings-cat {
  border: 1px solid rgba(255, 255, 255, 0.85);
  background: rgba(255, 252, 250, 0.75);
  color: #1e1e1e;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 800;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.site-settings-cat small {
  background: rgba(30, 30, 30, 0.06);
  border-radius: 999px;
  padding: 1px 7px;
  font-weight: 700;
}

.site-settings-cat.active {
  background: #1e1e1e;
  color: #fff;
}

.site-settings-cat.active small {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.site-settings-group {
  margin-bottom: 22px;
}

.site-settings-group-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.site-settings-group-icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(235, 61, 96, 0.12);
  color: #eb3d60;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.site-settings-group-head h2 {
  font-size: 18px;
  font-weight: 800;
  margin: 0;
  color: #1e1e1e;
}

.site-settings-group-head p {
  margin: 2px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.site-settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 12px;
}

.site-setting-card {
  background:
    radial-gradient(90px 70px at 118% -20%, rgba(235, 61, 96, 0.14), transparent 70%),
    #fff;
  border: 1px solid #f3e7e3;
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 8px 20px rgba(30, 30, 30, 0.05);
  min-height: 148px;
  display: flex;
  flex-direction: column;
}

.site-setting-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.site-setting-top h3 {
  font-size: 15px;
  font-weight: 800;
  margin: 0;
  color: #1e1e1e;
  line-height: 1.6;
}

.site-setting-actions {
  display: flex;
  gap: 6px;
}

.site-setting-actions a,
.site-setting-actions button {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 0;
  background: #f7f1ed;
  color: #1e1e1e;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.site-setting-hint {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.7;
  margin: 8px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.site-setting-value {
  margin-top: auto;
  padding-top: 12px;
  color: #1e1e1e;
  font-weight: 700;
  line-height: 1.7;
  word-break: break-word;
}

.site-setting-value img {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
}

.site-setting-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 800;
}

.site-setting-pill.is-on {
  background: rgba(18, 140, 90, 0.12);
  color: #0f7a4d;
}

.site-setting-pill.is-off,
.site-setting-pill.is-neutral {
  background: rgba(30, 30, 30, 0.06);
  color: #4b5563;
}

.site-setting-flags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.site-setting-pill.is-disabled {
  background: rgba(180, 35, 24, 0.12);
  color: #b42318;
}

.site-setting-actions button.site-setting-power.is-on {
  color: #0f7a4d;
}

.site-setting-actions button.site-setting-power.is-off {
  color: #b42318;
}

.site-settings-empty {
  background: #fff;
  border-radius: 18px;
  padding: 28px;
  text-align: center;
  color: #6b7280;
}

@media (max-width: 767px) {
  .site-settings-hero {
    flex-direction: column;
    align-items: flex-start;
    padding: 18px;
    border-radius: 18px;
  }

  .site-settings-hero h1 {
    font-size: 22px;
  }

  .site-settings-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
